import { trackError } from '@billy/track-error'
import { format } from 'date-fns'

import { CustomEvent } from '../../../enums/customEvent'
import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { dispatchCustomEvent } from '../../../utils/customEvent'
import type { ReviewEntryDataFormValues } from '../types/getReviewEntryDataFormSchema'
import { parseReceiptDate } from './parseReceiptDate'

export const REVIEW_ENTRY_DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000

export interface ReviewEntryDraftKey {
  entryId: string
  organizationId: string
  sourceFingerprint?: string
}

export const getReviewEntryDraftStorageKey = ({ entryId, organizationId }: ReviewEntryDraftKey): string =>
  `${LocalStorageKeys.ReviewEntryDraft}-${organizationId}-${entryId}`

const getReviewEntryDraftKeyPrefix = (): string => `${LocalStorageKeys.ReviewEntryDraft}-`

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

interface ReviewEntryDraftEnvelope {
  expiresAt: number
  sourceFingerprint?: string
  values: Record<string, unknown>
}

const isDraftEnvelope = (value: unknown): value is ReviewEntryDraftEnvelope =>
  isRecord(value) &&
  typeof value.expiresAt === 'number' &&
  isRecord(value.values) &&
  (value.sourceFingerprint === undefined || typeof value.sourceFingerprint === 'string')

const formatDraftDate = (value: unknown): string | null | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return format(value, 'yyyy-MM-dd')
  }

  if (typeof value === 'string' || value === null || value === undefined) {
    return value
  }

  return undefined
}

const reviveDraftDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value
  }

  if (typeof value !== 'string' || value.length === 0) {
    return undefined
  }

  return parseReceiptDate(value)
}

const reviveOptionalDraftDate = (value: unknown): Date | null | undefined => {
  if (value === null) {
    return null
  }

  if (value === undefined) {
    return undefined
  }

  return reviveDraftDate(value) ?? null
}

const serializeDraftFormValues = (values: unknown): Record<string, unknown> => {
  if (!isRecord(values)) {
    return {}
  }

  return {
    ...values,
    dueDate: formatDraftDate(values.dueDate),
    receiptDate: formatDraftDate(values.receiptDate),
  }
}

export const serializeReviewEntryDraft = (values: unknown, now = Date.now(), sourceFingerprint?: string): string =>
  JSON.stringify({
    expiresAt: now + REVIEW_ENTRY_DRAFT_TTL_MS,
    sourceFingerprint,
    values: serializeDraftFormValues(values),
  })

const reviveDraftFormValues = (values: Record<string, unknown>): Partial<ReviewEntryDataFormValues> => ({
  ...(values as Partial<ReviewEntryDataFormValues>),
  dueDate: reviveOptionalDraftDate(values.dueDate),
  receiptDate: reviveDraftDate(values.receiptDate),
})

const isQuotaExceededError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')

const reportedDraftStorageFailures = new Set<string>()

const getErrorName = (error: unknown): string => (error instanceof Error ? error.name : 'unknown')

const trackDraftStorageError = (message: string, error: unknown): void => {
  const reportKey = `${message}:${getErrorName(error)}`

  if (reportedDraftStorageFailures.has(reportKey)) {
    return
  }

  reportedDraftStorageFailures.add(reportKey)

  const trackedError = new Error(`${message} (${getErrorName(error)})`)

  if (error instanceof Error) {
    trackedError.cause = error
  }

  trackError(trackedError)
}

const notifyReviewEntryDraftChanged = (entryId?: string): void => {
  dispatchCustomEvent(CustomEvent.ReviewEntryDraftChanged, entryId ? { entryId } : undefined)
}

const readStoredDraft = (
  raw: string,
  storageKey: string,
  now: number,
  sourceFingerprint?: string,
): Partial<ReviewEntryDataFormValues> | undefined => {
  const parsed: unknown = JSON.parse(raw)

  if (!isDraftEnvelope(parsed) || parsed.expiresAt <= now) {
    globalThis.localStorage.removeItem(storageKey)
    return undefined
  }

  if (sourceFingerprint !== undefined && parsed.sourceFingerprint !== sourceFingerprint) {
    globalThis.localStorage.removeItem(storageKey)
    return undefined
  }

  return reviveDraftFormValues(parsed.values)
}

export const pruneExpiredReviewEntryDrafts = (now = Date.now()): void => {
  try {
    const prefix = getReviewEntryDraftKeyPrefix()
    const keysToRemove: string[] = []

    for (let index = 0; index < globalThis.localStorage.length; index += 1) {
      const key = globalThis.localStorage.key(index)

      if (!key?.startsWith(prefix)) {
        continue
      }

      const raw = globalThis.localStorage.getItem(key)

      if (!raw) {
        continue
      }

      try {
        const parsed: unknown = JSON.parse(raw)

        if (!isDraftEnvelope(parsed) || parsed.expiresAt <= now) {
          keysToRemove.push(key)
        }
      } catch {
        keysToRemove.push(key)
      }
    }

    for (const key of keysToRemove) {
      globalThis.localStorage.removeItem(key)
    }
  } catch (error) {
    trackDraftStorageError('Review entry draft: failed to prune expired drafts', error)
  }
}

export const getReviewEntryDraft = ({
  entryId,
  organizationId,
  sourceFingerprint,
}: ReviewEntryDraftKey): Partial<ReviewEntryDataFormValues> | undefined => {
  const storageKey = getReviewEntryDraftStorageKey({ entryId, organizationId })

  try {
    const raw = globalThis.localStorage.getItem(storageKey)

    if (!raw) {
      return undefined
    }

    return readStoredDraft(raw, storageKey, Date.now(), sourceFingerprint)
  } catch (error) {
    trackDraftStorageError('Review entry draft: failed to read stored value', error)

    try {
      globalThis.localStorage.removeItem(storageKey)
    } catch {
      // the read already failed; dropping the key is best-effort
    }

    return undefined
  }
}

const writeReviewEntryDraft = ({ entryId, organizationId }: ReviewEntryDraftKey, serialized: string): void => {
  globalThis.localStorage.setItem(getReviewEntryDraftStorageKey({ entryId, organizationId }), serialized)
  reportedDraftStorageFailures.clear()
  notifyReviewEntryDraftChanged(entryId)
}

export const saveReviewEntryDraft = (
  { entryId, organizationId, sourceFingerprint }: ReviewEntryDraftKey,
  values: unknown,
): void => {
  const draftKey = { entryId, organizationId, sourceFingerprint }

  try {
    writeReviewEntryDraft(draftKey, serializeReviewEntryDraft(values, Date.now(), sourceFingerprint))
  } catch (error) {
    if (isQuotaExceededError(error)) {
      pruneExpiredReviewEntryDrafts()

      try {
        writeReviewEntryDraft(draftKey, serializeReviewEntryDraft(values, Date.now(), sourceFingerprint))
        return
      } catch (retryError) {
        trackDraftStorageError('Review entry draft: storage quota exceeded', retryError)
        return
      }
    }

    trackDraftStorageError('Review entry draft: failed to save', error)
  }
}

export const removeReviewEntryDraft = ({ entryId, organizationId }: ReviewEntryDraftKey): void => {
  try {
    globalThis.localStorage.removeItem(getReviewEntryDraftStorageKey({ entryId, organizationId }))
    notifyReviewEntryDraftChanged(entryId)
  } catch (error) {
    trackDraftStorageError('Review entry draft: failed to remove', error)
  }
}
